import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { customizeValidator } from "@rjsf/validator-ajv8";
import JSONSchemaForm from "@rjsf/core";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import customFormats from "@beeldit/core/custom-validations/custom-validation";
import useProcessSchema from "@beeldit/core/hooks/useProcessSchema";
import widgets from "@beeldit/core/widgets/widgets";
import MultidimensionalCustomFormTemplate from "@beeldit/core/templates/MultidimensionalCustomFormTemplate";

interface Prop {
  formConfig: {
    formRef: any;
    onSubmit: any;
    context: any;
    action: string;
    className?: string;
    formDefaultValues?: any;
  };
}

interface Context {
  element: any;
  setElement: (element: any) => any;
  backendFormErrors: any;
}

function CustomerForm(props: Prop) {
  const { formRef, onSubmit, context, action, className, formDefaultValues } =
    props.formConfig;
  const { element, setElement, backendFormErrors } =
    useContext<Context>(context);

  const { t } = useTranslation();

  const [finalSchema, setFinalSchema] = useState<RJSFSchema>({});
  const translations = {
    name:
      formDefaultValues?.user?.role === "dealer" ? t("dealer_name") : t("name"),
    surnames: t("surnames"),
    email: t("email"),
    password: t("password"),
    password_confirmation: t("password_confirmation"),
    alternative_email: t("alternative_email"),
    phone: t("phone"),
    street: t("street"),
    city: t("city"),
    postal_code: t("postal_code"),
    observations: t("observations"),
    discount: t("discount"),
    state: t("state"),
    country: t("country"),
    create_address: t("customer_create_address"),
    create_invoice_account: t("create_invoice_account"),
  };

  let schema: any = {
    type: "object",
    properties: {
      user: {
        type: "object",
        title: "",
        properties: {
          name: {
            type: "string",
            title: translations.name,
          },
          surnames: {
            type: ["string", "null"],
            title: translations.surnames,
          },
          email: {
            type: "string",
            title: translations.email,
          },
          password: {
            type: "string",
            title: translations.password,
            show: action == "create",
          },
          password_confirmation: {
            type: "string",
            title: translations.password_confirmation,
            show: action == "create",
          },
        },
      },
      alternative_email: {
        type: ["string", "null"],
        title: translations.alternative_email,
        show: false,
      },
      phone: {
        type: ["string", "null"],
        title: translations.phone,
      },
      street: {
        type: ["string", "null"],
        title: translations.street,
      },
      city: {
        type: ["string", "null"],
        title: translations.city,
      },
      postal_code: {
        type: ["string", "null"],
        title: translations.postal_code,
      },
      state: {
        type: ["string", "null"],
        title: translations.state,
      },
      country: {
        type: ["string", "null"],
        title: translations.country,
      },
      observations: {
        type: ["string", "null"],
        title: translations.observations,
      },
      discount: {
        type: "number",
        title: translations.discount,
        minimun: 0,
        maximun: 0,
        default: 0,
        show: formDefaultValues?.user.role !== "customer",
      },
      create_address: {
        type: ["boolean"],
        title: translations.create_address,
        show: action == "create",
      },
      create_invoice_account: {
        type: ["boolean"],
        title: translations.create_invoice_account,
        show: action == "create",
      },
    },
  };

  const uiSchema = {
    "ui:classNames": className,
    "ui:submitButtonOptions": {
      props: {
        disabled: false,
        className: "btn btn-info",
      },
      norender: true,
    },
    user: {
      password: {
        "ui:widget": "password",
      },
      password_confirmation: {
        "ui:widget": "password",
      },
      colsSchema: [
        {
          cols: "col-12",
          elements: [
            [
              {
                name: "name",
                cols: "col-6",
              },
              {
                name: "surnames",
                cols: "col-6",
              },
              {
                name: "email",
                cols: "col-6",
              },
              {
                name: "password",
                cols: "col-6",
              },
              {
                name: "password_confirmation",
                cols: "col-6",
              },
            ],
          ],
        },
      ],
    },
    observations: {
      "ui:widget": "textarea",
    },
    colsSchema: [
      {
        cols: "col-12",
        elements: [
          [
            {
              name: "user",
              cols: "col-12",
            },
            // {
            //   name: "alternative_email",
            //   cols: "col-6",
            // },
            {
              name: "phone",
              cols: "col-6",
            },
            {
              name: "street",
              cols: "col-6",
            },
            {
              name: "city",
              cols: "col-6",
            },
            {
              name: "postal_code",
              cols: "col-6",
            },
            {
              name: "state",
              cols: "col-6",
            },
            {
              name: "country",
              cols: "col-6",
            },
            {
              name: "observations",
              cols: "col-12",
            },
            {
              name: "discount",
              cols: "col-6",
            },
            {
              name: "create_address",
              cols: "col-12",
            },
            {
              name: "create_invoice_account",
              cols: "col-12",
            },
          ],
        ],
      },
    ],
  };
  const customWidgets = {
    ...widgets,
  };
  const handleChange = (type: any) => {
    /** Este método de momento es necesario aunque en realidad no tenga sentido, pero se debe a un bug de la libreria */
    // Actualizar el estado solo si no hay errores de validación
    setElement(type.formData);
    return console.log.bind(console, type);
  };
  const log = (type: any) => {
    return console.log.bind(console, type);
  };

  const formats = customFormats;
  const validator = customizeValidator({ customFormats: formats });

  const processSchemaFunction = useProcessSchema();

  useEffect(() => {
    setFinalSchema(processSchemaFunction(schema, element));
  }, [element]);

  const formTemplate = MultidimensionalCustomFormTemplate;

  return (
    <JSONSchemaForm
      noHtml5Validate
      showErrorList={false}
      ref={formRef}
      schema={finalSchema}
      uiSchema={uiSchema}
      widgets={customWidgets}
      formData={element}
      validator={validator}
      onChange={handleChange}
      onSubmit={onSubmit}
      onError={log("errors")}
      extraErrors={backendFormErrors}
      templates={{
        ObjectFieldTemplate: formTemplate,
      }}
    />
  );
}

export default CustomerForm;
